// What the read-aloud button says (24 Oct 2026, Year 1 first): the question,
// then its answer options in order, and on the help screen the hint. It is
// built from what the screen already shows a child, and nothing else: never
// the answer, the working or the mistake notes, which are not in the
// question's content at all, and never a picture's own words.
//
// Sums are said as words, the way a teacher reads them aloud ("6 + 3" as
// "6 plus 3", "−" as "take away", "☐" as "box", "$5" as "5 dollars", "50c"
// as "50 cents"), so a voice never says "dash" or "dollar sign".

const LETTERS = 'abcdefghijklmnopqrstuvwxyz';

// True where the browser can speak (the button is shown only then).
export function canReadAloud(): boolean {
  return (
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof window.SpeechSynthesisUtterance === 'function'
  );
}

// The signs in a question, as words.
export function spokenWords(text: string): string {
  return (
    text
      // A box to type in, inside the sentence: "[a]" is "box a".
      .replace(/\[([a-z])\]/g, ' box $1 ')
      .replace(/☐/g, ' box ')
      .replace(
        /\$(\d+)(?:\.(\d{2}))?/g,
        (_all, dollars: string, cents = '') => {
          const whole = `${dollars} ${dollars === '1' ? 'dollar' : 'dollars'}`;
          return cents === '' || cents === '00'
            ? whole
            : `${whole} ${String(Number(cents))} cents`;
        },
      )
      .replace(/\b(\d+)c\b/g, (_all, cents: string) =>
        cents === '1' ? '1 cent' : `${cents} cents`,
      )
      .replace(/\+/g, ' plus ')
      .replace(/−/g, ' take away ')
      // A hyphen is take away only between numbers or standing alone, so
      // "ice-cream" and "yo-yo" stay as they are.
      .replace(/(\d)\s*-\s*(?=\d)/g, '$1 take away ')
      .replace(/\s-\s/g, ' take away ')
      .replace(/=/g, ' equals ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

// A sentence ends with a full stop (or its own ? or !), so the voice pauses
// between the question and each option.
function sentence(text: string): string {
  const words = spokenWords(text);
  if (words === '') return '';
  return /[.?!:]$/.test(words) ? words : `${words}.`;
}

type Entry = { label: string; picture: boolean };

function entries(list: unknown): Entry[] {
  if (!Array.isArray(list)) return [];
  return list.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const record = entry as Record<string, unknown>;
    const label = typeof record.label === 'string' ? record.label.trim() : '';
    const picture = record.diagram !== undefined;
    return label === '' && !picture ? [] : [{ label, picture }];
  });
}

// "a: 6 apples." for each option, in the order shown. A picture with no
// words is "a: picture." (the picture itself is not read out).
function lettered(list: Entry[]): string[] {
  return list.map((entry, index) => {
    const letter = LETTERS[index] ?? '';
    return sentence(
      `${letter}: ${entry.label === '' ? 'picture' : entry.label}`,
    );
  });
}

function plain(list: Entry[]): string {
  return list
    .map((entry, index) =>
      entry.label === ''
        ? `picture ${LETTERS[index] ?? ''}`
        : spokenWords(entry.label),
    )
    .join(', ');
}

// The words for one practice question, from its content as the screen
// gets it. Empty when there is no question to read.
export function readAloudScript(content: unknown): string {
  if (typeof content !== 'object' || content === null) return '';
  const record = content as Record<string, unknown>;
  const prompt = typeof record.prompt === 'string' ? record.prompt : '';
  const parts = [sentence(prompt)];
  if (Array.isArray(record.left)) {
    // Match: the items, then the partners.
    parts.push(sentence(plain(entries(record.left))));
    parts.push(sentence(`Partners: ${plain(entries(record.right))}`));
  } else if (Array.isArray(record.groups) && Array.isArray(record.items)) {
    // Sort: the group names, then the items.
    parts.push(sentence(`Groups: ${plain(entries(record.groups))}`));
    parts.push(sentence(plain(entries(record.items))));
  } else if (Array.isArray(record.options)) {
    parts.push(...lettered(entries(record.options)));
  } else if (Array.isArray(record.items)) {
    parts.push(...lettered(entries(record.items)));
  } else if (Array.isArray(record.boxes)) {
    // Boxes under the sentence, each by its letter and label. A box marked
    // in the sentence was read with it.
    for (const box of record.boxes as unknown[]) {
      if (typeof box !== 'object' || box === null) continue;
      const { id, label } = box as Record<string, unknown>;
      if (typeof id !== 'string' || prompt.includes(`[${id}]`)) continue;
      const words = typeof label === 'string' ? label.trim() : '';
      parts.push(sentence(words === '' ? `box ${id}` : `box ${id}: ${words}`));
    }
  }
  return parts.filter((part) => part !== '').join(' ');
}
